/* ─────────────────────────────────────────────────────────────────
   MLW Quiz Progress Bar
   ─────────────────────────────────────────────────────────────────
   Thin bar that fills proportionally with progress. Two brand
   toggles wire up the two variants:

     • showCounter      — true shows "X of Y" text (TMT); false
                          hides count entirely (MLW — branching
                          paths shouldn't leak through the UI).
     • totalStepsStrategy
         "fixed"    → denominator is total question count
         "estimate" → denominator is visible + answered count
                      (for branching flows where the actual total
                      is unknown until every showWhen resolves).

   API
     MLWQuizProgress.mount(container, options)
       options = {
         showCounter: bool,
         totalStepsStrategy: "fixed" | "estimate",
         counterLabel: "{current} of {total}",   // template
       }
     MLWQuizProgress.update(instance, { current, total })
     MLWQuizProgress.hide(instance) / show(instance)
   ───────────────────────────────────────────────────────────────── */

(function (window) {
  "use strict";

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      for (var k in attrs) {
        if (!Object.prototype.hasOwnProperty.call(attrs, k)) continue;
        if (k === "class") node.className = attrs[k];
        else if (k in node) node[k] = attrs[k];
        else node.setAttribute(k, attrs[k]);
      }
    }
    if (children) {
      for (var i = 0; i < children.length; i++) {
        var c = children[i];
        if (c == null) continue;
        if (typeof c === "string") node.appendChild(document.createTextNode(c));
        else node.appendChild(c);
      }
    }
    return node;
  }

  function mount(container, options) {
    options = options || {};
    var showCounter = options.showCounter !== false;
    var strategy = options.totalStepsStrategy || "fixed";
    var template = options.counterLabel || "{current} of {total}";

    /* Outer wrapper — width aligned with MLW question card (560px).
       No top padding: the whole progress+card+back stack is vertically
       centered by the parent quiz container, so the bar should sit
       directly above the card with only the 16px bottom margin. */
    var wrap = el("div", {
      class: "quiz-progress",
      style:
        "padding:0 20px;max-width:560px;margin:0 auto 16px;" +
        "width:100%;box-sizing:border-box;",
      role: "progressbar",
      "aria-label": "Quiz progress",
      "aria-valuemin": "0",
      "aria-valuemax": "100",
      "aria-valuenow": "0",
    });

    /* Explicit 4px height + 999px radius per JSX (not via
       --quiz-radius-pill which is 10px and reads as square on
       a 4px-tall track). */
    var track = el("div", {
      class: "quiz-progress__track",
      style:
        "position:relative;height:4px;" +
        "background:var(--quiz-progress-bg, #ddd6f3);" +
        "border-radius:999px;overflow:hidden;",
    });
    track.textContent = '\u200B';

    var fill = el("div", {
      class: "quiz-progress__fill",
      style:
        "position:absolute;top:0;left:0;bottom:0;width:0%;" +
        "background:var(--quiz-progress-fill, #8b7fc7);" +
        "border-radius:999px;" +
        "transition:width 0.35s ease;",
    });
    fill.textContent = '\u200B';

    track.appendChild(fill);
    wrap.appendChild(track);

    var counter = null;
    if (showCounter) {
      counter = el("p", {
        class: "quiz-progress__counter",
        style:
          "margin:8px 0 0;text-align:right;" +
          "font-family:var(--quiz-font-body);font-size:11px;" +
          "color:var(--quiz-text-light);letter-spacing:1px;",
      }, [""]);
      wrap.appendChild(counter);
    }

    container.appendChild(wrap);

    return {
      root: wrap,
      fill: fill,
      counter: counter,
      showCounter: showCounter,
      strategy: strategy,
      template: template,
    };
  }

  function update(instance, data) {
    if (!instance || !data) return;
    var current = Math.max(0, data.current || 0);
    var total = Math.max(1, data.total || 1);

    /* "estimate" strategy never exposes a misleadingly precise total
       in the counter — it still drives the bar fill, but the
       counter text is hidden even when showCounter is true. */
    var pct = Math.min((current / total) * 100, 100);
    if (window.console && console.log) {
      console.log("[MLW Quiz Progress] update", { current: current, total: total, pct: pct });
    }
    instance.fill.style.width = pct + "%";
    instance.root.setAttribute("aria-valuenow", String(Math.round(pct)));

    if (instance.counter) {
      if (instance.strategy === "estimate") {
        instance.counter.textContent = "";
      } else {
        instance.counter.textContent = instance.template
          .replace("{current}", String(current))
          .replace("{total}", String(total));
      }
    }
  }

  function hide(instance) {
    if (instance && instance.root) instance.root.style.display = "none";
  }
  function show(instance) {
    if (instance && instance.root) instance.root.style.display = "";
  }

  window.MLWQuizProgress = {
    mount: mount,
    update: update,
    hide: hide,
    show: show,
  };
})(window);
